import { FileWarning } from "lucide-react";
import React from "react";

import { SecureHtmlViewer } from "~/components/secure-html-viewer";
import { StatusCard } from "~/components/status-card";
import { useFileContent } from "~/hooks/use-file-content";
import { ThemedLoadingSpinner } from "~/theming/feature/themed-loading-spinner";

interface HtmlFileViewerProps {
  content: string | Uint8Array;
  s3Url?: string | null;
}

export const HtmlFileViewer = ({ content, s3Url }: HtmlFileViewerProps) => {
  // Always render via srcdoc (never iframe src) so the injected CSP covers the document.
  const source = s3Url && URL.canParse(s3Url) ? s3Url : content;
  const isInlineHtml = typeof source === "string" && !URL.canParse(source);
  const htmlContentState = useFileContent(source, !!source && !isInlineHtml, decodeHtml);

  if (!source) {
    return null;
  }

  if (isInlineHtml) {
    return <SecureHtmlViewer htmlContent={source} />;
  }

  if (htmlContentState.status === "loading") {
    return (
      <StatusCard
        icon={<ThemedLoadingSpinner width="24px" height="24px" />}
        title="Loading file"
        description="Preparing a preview..."
      />
    );
  }

  if (htmlContentState.status === "error") {
    return (
      <StatusCard
        icon={<FileWarning />}
        title="Failed to load file"
        description="Failed to load the file. Please try again later."
      />
    );
  }

  return <SecureHtmlViewer htmlContent={htmlContentState.value} />;
};

// HTML declares its own encoding (a BOM, or <meta charset> in the first 1024 bytes). Decoding every file as
// UTF-8 would garble legacy Windows-1252 or Shift-JIS reports, which a direct iframe load used to handle.
function decodeHtml(bytes: Uint8Array): string {
  return new TextDecoder(sniffHtmlEncoding(bytes)).decode(bytes);
}

function sniffHtmlEncoding(bytes: Uint8Array): string {
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return "utf-8";
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return "utf-16le";
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return "utf-16be";
  // Like the HTML encoding prescan: skip comments, which end at the first "-->" even when its dashes are
  // the opener's own ("<!-->"), and one still open at byte 1024 runs to the end. Read a charset only from a
  // <meta charset> attribute or the content of a <meta http-equiv="content-type">, where "<meta" is followed
  // by whitespace or "/" (so not a custom element such as <meta-x>).
  const head = String.fromCharCode(...bytes.subarray(0, 1024)).replace(/<!(?=--)[\s\S]*?(?:-->|$)/g, "");
  const metaTags = head.match(/<meta[\t\n\f\r /](?:"[^"]*"|'[^']*'|[^"'>])*/gi) ?? [];
  const declared = metaTags.map(declaredCharset).find(Boolean);
  return (declared && htmlEncoding(declared)) ?? "utf-8";
}

// HTML reads a declared UTF-16 as UTF-8 (a real UTF-16 file starts with a BOM) and x-user-defined as
// Windows-1252.
function htmlEncoding(label: string): string | undefined {
  const encoding = supportedEncoding(label);
  if (encoding === "utf-16le" || encoding === "utf-16be") return "utf-8";
  if (encoding === "x-user-defined") return "windows-1252";
  return encoding;
}

function declaredCharset(metaTag: string): string | undefined {
  const attributes = metaAttributes(metaTag);
  const charset = attributes.get("charset")?.trim();
  if (charset) return charset;
  if (attributes.get("http-equiv")?.trim().toLowerCase() !== "content-type") return undefined;
  return /charset\s*=\s*["']?([\w.:-]+)/i.exec(attributes.get("content") ?? "")?.[1];
}

// Attribute names and values as the tokenizer reads them, so "charset=" text inside another attribute's
// quoted value isn't mistaken for a declaration. The first occurrence of a name wins, as in HTML.
const META_ATTRIBUTE = /([^\s"'=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?/g;

function metaAttributes(metaTag: string): Map<string, string> {
  const entries = [...metaTag.slice("<meta".length).matchAll(META_ATTRIBUTE)].map(
    ([, name = "", doubleQuoted, singleQuoted, unquoted]) =>
      [name.toLowerCase(), doubleQuoted ?? singleQuoted ?? unquoted ?? ""] as const,
  );
  return new Map(entries.reverse());
}

function supportedEncoding(label: string): string | undefined {
  try {
    return new TextDecoder(label).encoding;
  } catch {
    return undefined;
  }
}
